import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Alert, Button, Card, Form, Input, Layout, Typography } from "antd";
import { Lock, Mail, Plane } from "lucide-react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { loginUser } from "../../store/authSlice";

import "./LoginPage.css";

const { Header, Content } = Layout;
const { Title, Text, Paragraph } = Typography;

type CredentialsForm = {
  emailOrPhone: string;
  password: string;
};

export default function LoginPage() {
  const navigate = useNavigate();

  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch<AppDispatch>();

  const handleCredentialsSubmit = async (values: CredentialsForm) => {
    setError("");
    setIsLoading(true);

    try {
      await dispatch(
        loginUser({
          identifier: values.emailOrPhone,
          password: values.password,
        })
      ).unwrap();

      navigate("/admin/calendar");
    } catch (err: any) {
      setError(
        typeof err === "string"
          ? err
          : err?.message || "Login failed. Please check your credentials."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout className="login-page">
      <Header className="login-header">
        <Link to="/" className="login-logo">
          <Plane size={24} className="login-logo-icon" />
          <span>Hairplane</span>
        </Link>
      </Header>

      <Content className="login-content">
        <div className="login-wrapper">
          <Card className="login-card">
            <div className="login-card-header">
              <div className="login-icon login-icon-blue">
                <Lock size={32} />
              </div>

              <Title level={2}>Welcome Back</Title>
              <Paragraph>Sign in to access your dashboard</Paragraph>
            </div>

            <Form<CredentialsForm>
              layout="vertical"
              requiredMark={false}
              onFinish={handleCredentialsSubmit}
            >
              <Form.Item
                label="Email or Phone Number"
                name="emailOrPhone"
                rules={[
                  {
                    required: true,
                    message: "Please enter your email or phone number",
                  },
                ]}
              >
                <Input
                  size="large"
                  prefix={<Mail size={18} />}
                  placeholder="you@example.com or (555) 123-4567"
                />
              </Form.Item>

              <Form.Item
                label="Password"
                name="password"
                rules={[
                  {
                    required: true,
                    message: "Please enter your password",
                  },
                  {
                    min: 6,
                    message: "Password must be at least 6 characters",
                  },
                ]}
              >
                <Input.Password
                  size="large"
                  prefix={<Lock size={18} />}
                  placeholder="Enter your password"
                />
              </Form.Item>

              {error && (
                <Alert
                  type="error"
                  message={error}
                  showIcon
                  className="login-alert"
                />
              )}

              <div className="login-forgot-row">
                <Button type="link" className="login-link-button">
                  Forgot password?
                </Button>
              </div>

              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={isLoading}
                className="login-submit-button"
              >
                Continue
              </Button>
            </Form>
          </Card>

          <div className="login-footer-text">
            <Text>
              Don&apos;t have an account?{" "}
              <Link to="/demo">Request a demo</Link>
            </Text>
          </div>
        </div>
      </Content>
    </Layout>
  );
}
